import i18n from "@i18n/i18n";
import ExpandIcon from "@mui/icons-material/Add";
import CollapseIcon from "@mui/icons-material/Remove";
import { Box, IconButton, Tooltip } from "@mui/material";
import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarProps,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { useGridApiContext } from "@mui/x-data-grid-premium";
import { GridPrivateApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { useGridPrivateApiContext } from "@mui/x-data-grid/internals";
import PropTypes from "prop-types";
import { forwardRef } from "react";
import { useGridOptions } from "../../hooks/features/options/useGridOptions";
import { useGridRowVisibilitySelection } from "../../hooks/features/rowVisibilitySelection/useGridRowVisibilitySelection";
import useExpandAndCollapse from "../../hooks/useExpandAndCollapse";
import { useGridRootProps } from "../../hooks/useGridRootProps";
import { useSaveGridState } from "../../hooks/useSaveGridState";
import { GridAutosizeColumnsOptions } from "../../models/gridAutosizeColumns";
import { GridHeaderFiltersOptions } from "../../models/gridHeaderFilters";
import { GridResetStateOptions } from "../../models/gridResetState";
import { GridOptionsDisplayOptions, GridToolbarOptionsButtonMenu } from "./GridToolbarOptionsButtonMenu";
import GridToolbarSearchFilter, { GridToolbarSearchFilterProps } from "./GridToolbarSearchFilter";

export interface CustomToolbarProps extends GridToolbarProps {
  /**
   * The options to for autosizeColumns behavior.
   */
  autosizeColumnsOptions?: GridAutosizeColumnsOptions & GridOptionsDisplayOptions;
  /**
   * The options to for headerFilters behavior.
   */
  headerFiltersOptions?: GridHeaderFiltersOptions & GridOptionsDisplayOptions;
  /**
   * The options to for resetGridState behavior.
   */
  resetGridStateOptions?: GridResetStateOptions & GridOptionsDisplayOptions;
  /**
   * Show the search filter component.
   * @default false
   */
  showSearchFilter?: boolean;
  /**
   * Props passed to the search filter component.
   */
  searchFilterProps?: GridToolbarSearchFilterProps;
}

const GridToolbarCustom = forwardRef<HTMLDivElement, CustomToolbarProps>((props: CustomToolbarProps, ref: any) => {
  console.log("CustomToolbar -> props: ", props);
  const {
    className,
    csvOptions,
    printOptions,
    excelOptions,
    showQuickFilter = true,
    quickFilterProps = {},
    showSearchFilter = false,
    searchFilterProps = {},
    autosizeColumnsOptions,
    headerFiltersOptions,
    resetGridStateOptions,
    ...other
  } = props as typeof props & { excelOptions: any };
  const privateApiRef = useGridPrivateApiContext<GridPrivateApiPremium>();
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const expandAndCollapse = useExpandAndCollapse(apiRef);

  // TODO: how do we get our custom hooks loaded into the grid?
  // TODO: maybe move these to a useCustomHooks bootstrap method
  useSaveGridState(privateApiRef);
  useGridRowVisibilitySelection(privateApiRef, rootProps);
  useGridOptions(privateApiRef);
  // TODO: how do we get our custom hooks loaded into the grid?

  const handleExpandAll = async (event: any) => {
    await expandAndCollapse?.onExpandAll(event);
  };

  const handleCollapseAll = async (event: any) => {
    await expandAndCollapse?.onCollapseAll(event);
  };

  if (
    rootProps.disableColumnFilter &&
    rootProps.disableColumnSelector &&
    rootProps.disableDensitySelector &&
    rootProps.disableExpandAndCollapse &&
    !showQuickFilter &&
    !showSearchFilter
  ) {
    return null;
  }

  return (
    <GridToolbarContainer sx={{ padding: "2px" }} {...other} ref={ref}>
      {!rootProps.disableExpandAndCollapse && (
        <Box sx={{ textAlign: "left", marginTop: 0 }}>
          <Tooltip title={i18n.t("custom-toolbar.expand-all-button.tooltip")}>
            <span>
              <IconButton aria-label="expand" size="small" onClick={handleExpandAll} disabled={expandAndCollapse?.expandAllDisabled}>
                <ExpandIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={i18n.t("custom-toolbar.collapse-all-button.tooltip")}>
            <span>
              <IconButton aria-label="collapse" size="small" onClick={handleCollapseAll} disabled={expandAndCollapse?.collapseAllDisabled}>
                <CollapseIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}
      <Box sx={{ flexGrow: 1 }} />
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector slotProps={{ tooltip: { title: "Change density" } }} />
      <GridToolbarExport
        csvOptions={csvOptions}
        printOptions={printOptions}
        excelOptions={excelOptions}
        slotProps={{
          tooltip: { title: "Export data" },
        }}
      />
      <GridToolbarOptionsButtonMenu autosizeColumnsOptions={autosizeColumnsOptions} headerFiltersOptions={headerFiltersOptions} resetGridOptions={resetGridStateOptions} />
      {showQuickFilter && <GridToolbarQuickFilter {...quickFilterProps} />}
      {showSearchFilter && <GridToolbarSearchFilter {...searchFilterProps} />}
    </GridToolbarContainer>
  );
});

// TODO: can we generate these from the TS types as the comment below says?
GridToolbarCustom.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  csvOptions: PropTypes.object,
  printOptions: PropTypes.object,
  /**
   * Props passed to the quick filter component.
   */
  quickFilterProps: PropTypes.shape({
    className: PropTypes.string,
    debounceMs: PropTypes.number,
    quickFilterFormatter: PropTypes.func,
    quickFilterParser: PropTypes.func,
    slotProps: PropTypes.object,
  }),
  /**
   * Show the quick filter component.
   * @default true
   */
  showQuickFilter: PropTypes.bool,
  /**
   * Props passed to the search filter component.
   */
  searchFilterProps: PropTypes.shape({
    className: PropTypes.string,
    quickFilterFormatter: PropTypes.func,
    quickFilterParser: PropTypes.func,
    debounceMs: PropTypes.number,
    collapsible: PropTypes.bool,
    expanded: PropTypes.bool,
    onExpandedChange: PropTypes.func,
    onSearchChange: PropTypes.func,
    onSearchApply: PropTypes.func,
    slotProps: PropTypes.object,
  }),
  /**
   * The props used for each slot inside.
   * @default {}
   */
  slotProps: PropTypes.object,
  sx: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])), PropTypes.func, PropTypes.object]),
} as any;

export { GridToolbarCustom };

